import { useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import useAuthActions from './actions';
import useAuthQueries from './queries';
import { useAuthState, useAuthDispatch } from './context';
import { isDev, normalizeScopes } from 'src/utils/utils';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { API_URL } from '../../config';
import { version } from '../../_git_version';
import { useSnackbar } from 'notistack';

//const frontend_version = GitCommit.logMessage
const frontend_version = version;

console.log('Frontend Version:', frontend_version);
if (frontend_version) {
  axios.defaults.headers.common['Accept'] =
    'application/json; version=' + frontend_version;
}

function urlWithRndQueryParam(url) {
  const urlArr = url.split('#');
  const urlQry = urlArr[0].split('?');
  const usp = new URLSearchParams(urlQry[1] || '');
  usp.set('_z', `${Date.now()}`);
  urlQry[1] = usp.toString();
  urlArr[0] = urlQry.join('?');
  return urlArr.join('#');
}

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const msg_reload =
      'The server has changed version since you opened this ' +
      'page, you now need to reload this page.';
    if (error?.response?.status === 406 && window.confirm(msg_reload)) {
      window.location.href = urlWithRndQueryParam(window.location.href);
      window.location.reload();
      return;
    }

    if (error.config?.url?.includes('/auth/session')) {
      return Promise.reject(error);
    }

    // // Manually threat the existence of a session that was not terminated on logout
    if (error?.response?.status === 409) {
      axios.delete(`/_allauth/app/v1/auth/session`);
      window.location.replace(`${window.location.origin}${paths.auth.login}`);
    }
    throw error;
  }
);

export const useAuth = () => {
  const {
    rehydrated,
    authenticated,
    username,
    clientId,
    scopes,
    sessionToken,
  } = useAuthState();
  const dispatch = useAuthDispatch();
  const {
    loginSucceeded,
    logout: logoutAction,
    setSessionToken,
  } = useAuthActions(dispatch);
  const {
    register: registerRequest,
    sendResetPasswordLink: sendResetPasswordLinkRequest,
    resetPassword: passwordResetRequest,
    login: loginRequest,
    logout: logoutRequest,
    getTOTPStatus: getTOTPStatusRequest,
    authenticateWithTOTPCode: authenticateWithTOTPCodeRequest,
    activateAuthenticator: activateAuthenticatorRequest,
  } = useAuthQueries();
  const { enqueueSnackbar } = useSnackbar();

  const router = useRouter();

  const authenticatedHeaders = useMemo(
    () =>
      sessionToken
        ? {
            'X-Session-Token': sessionToken,
          }
        : {},
    [sessionToken]
  );

  const login = useCallback(
    ({ username: _username, password }) => {
      return loginRequest({ username: _username, password })
        .then(async (res) => {
          if (res?.data?.meta?.is_authenticated && res.data?.data?.user?.id) {
            setSessionToken({ sessionToken: res?.data?.meta?.session_token });
            const userResponse = await axios
              .get(`${API_URL}/user-management/users`, {
                params: {
                  username: _username,
                },
                headers: {
                  'X-Session-Token': res?.data?.meta?.session_token,
                },
              })
              .then((res) => {
                return res?.data?.results[0];
              });

            if (userResponse && userResponse.id) {
              loginSucceeded({
                username: userResponse.username
                  ? userResponse.username
                  : _username,
                scopes: normalizeScopes(userResponse.scopes),
                client: userResponse.company,
                userId: userResponse.id,
                sessionToken: res?.data?.meta?.session_token,
                isStaff: userResponse.is_staff,
              });
            } else {
              return Promise.reject('Something went wrong');
            }
          } else {
            alert('An error occurred');
          }
        })
        .catch(async (e) => {
          console.log(e);
          // TOTP
          if (e.response?.status === 401) {
            const routeData = {
              sessionToken: e.response?.data?.meta?.session_token,
              username: _username,
            };
            if (e.response?.data.meta.detail === 'totp_enrollment_required') {
              await getTOTPStatusRequest(routeData.sessionToken).catch((e) => {
                console.error({ e });
                router.push(`${paths.auth.authenticatorCode}`, {
                  url: e.response?.data.meta.totp_url,
                  ...routeData,
                });
              });
            } else {
              router.push(`${paths.auth.authenticatorCode}`, { ...routeData });
            }

            return (
              (await Promise.reject(e.response && e.response.data)) ||
              'Something went wrong'
            );
          } else {
            return Promise.reject('Something went wrong');
          }
        });
    },
    [
      loginRequest,
      loginSucceeded,
      setSessionToken,
      getTOTPStatusRequest,
      router,
    ]
  );

  const activateAuthenticator = useCallback(
    (code, sessionToken) => {
      return activateAuthenticatorRequest(code, sessionToken).then((res) => {
        if (res.status === 200) {
          logoutRequest(sessionToken);
          enqueueSnackbar(
            'Authenticator successfully activated. Proceed to login'
          );
          router.replace('/auth/login');
        }
      });
    },
    [activateAuthenticatorRequest, logoutRequest, router, enqueueSnackbar]
  );

  const authenticateWithTOTPCode = useCallback(
    (code, sessionToken) => {
      return authenticateWithTOTPCodeRequest(code, sessionToken).then(
        async (res) => {
          if (res.status === 200) {
            if (res?.data?.meta?.is_authenticated && res.data?.data?.user?.id) {
              setSessionToken({ sessionToken: res?.data?.meta?.session_token });
              const userResponse = await axios
                .get(`${API_URL}/user-management/users`, {
                  params: {
                    username: res.data?.data?.user?.username,
                  },
                  headers: {
                    'X-Session-Token': res?.data?.meta?.session_token,
                  },
                })
                .then((res) => {
                  return res?.data?.results[0];
                });

              if (userResponse && userResponse.id) {
                loginSucceeded({
                  username: userResponse.username
                    ? userResponse.username
                    : res.data?.data?.user?.username,
                  scopes: normalizeScopes(userResponse.scopes),
                  client: userResponse.company, // Is company supposed to be the client?,
                  userId: userResponse.id,
                  sessionToken: res?.data?.meta?.session_token,
                  isStaff: userResponse.is_staff,
                });
              } else {
                return Promise.reject('Something went wrong');
              }
            } else {
              alert('An error occurred');
            }
          }
        }
      );
    },
    [setSessionToken, loginSucceeded, authenticateWithTOTPCodeRequest]
  );

  const register = useCallback(
    ({
      company,
      first_name,
      last_name,
      email,
      username: _username,
      password,
    }) => {
      return registerRequest({
        company,
        first_name,
        last_name,
        email,
        username: _username,
        password,
      })
        .then((res) => {
          if (res?.status === 201) {
            // Auto login
            loginRequest({
              username: _username,
              password,
            }).then((res) => {
              loginSucceeded({
                username: res.username ? res.username : _username,
                scopes: res.scopes,
                client: res.company,
              });
            });
          } else {
            throw new Error('An error occurred');
          }
        })
        .catch((e) => {
          console.log(e);
          if (e.response?.status === 401) {
            alert('Unauthorized, please check your credentials.');
          } else {
            if (e.response && e.response.data) {
              for (const error_message of e.response.data.errors) {
                alert(error_message);
              }
            } else {
              alert('An error occurred');
            }
          }
          throw e;
        });
    },
    [registerRequest, loginSucceeded, loginRequest]
  );

  const sendResetPasswordLink = useCallback(
    ({ email }) => {
      return sendResetPasswordLinkRequest({ email })
        .then(() => {
          console.log(
            'You will receive an email shortly. Click on the link it contains to reset your password.'
          );
        })
        .catch((e) => {
          if (e.response.status === 500) {
            return Promise.reject('Something went wrong');
          } else {
            return (
              Promise.reject(e.response && e.response.data) ||
              'Something went wrong'
            );
          }
        });
    },
    [sendResetPasswordLinkRequest]
  );

  const resetPassword = useCallback(
    ({ key, password }, config) => {
      return passwordResetRequest({
        key,
        password,
        config,
      })
        .then(() => {
          router.push(paths.auth.login);
        })
        .catch((e) => {
          if (e.response?.status === 400 && e.response.data?.password) {
            return (
              Promise.reject(
                JSON.stringify(e.response.data.password, null, 2)
              ) || 'Something went wrong'
            );
          } else {
            return (
              Promise.reject(
                e.response && e.response.data && e.response.data.errors
              ) || 'Something went wrong'
            );
          }
        });
    },
    [passwordResetRequest, router]
  );

  const logout = useCallback(async () => {
    try {
      await logoutRequest(sessionToken);
    } catch (error) {
      console.log(error);
    }
    router.push(paths.auth.login);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {}, []);

  const authenticatedRequest = useCallback(
    (method, url, options = {}, data) => {
      if (!options?.headers?.['X-Session-Token']) {
        options.headers = {
          ...(options.headers || {}),
          ...authenticatedHeaders,
        };
      }

      let promise;
      if (method === 'get') {
        promise = axios.get(url, options);
      } else if (method === 'post') {
        promise = axios.post(url, data, options);
      } else if (method === 'put') {
        promise = axios.put(url, data, options);
      } else if (method === 'patch') {
        promise = axios.patch(url, data, options);
      } else if (method === 'delete') {
        promise = axios.delete(url, options);
      } else {
        return Promise.reject({
          message: `Method ${method} not handled yet`,
        });
      }

      return promise.catch((error) => {
        if (isDev) {
          console.log(method, url, error);
        }

        if ([401, 403, 409, 410].includes(error?.response?.status)) {
          // TODO: Delete cookie
          // TODO: Redirect to login
          // window.location.replace(urlWithRndQueryParam(`${window.location.origin}/${paths.auth.jwt.login}`));
          if (authenticated) {
            logoutAction();
            axios.delete(`/_allauth/app/v1/auth/session`);
          }
          router.push(paths.auth.login);
        }

        // Important: don't forget to always re-throw the error,
        // otherwise callers of authenticatedRequest() will get
        // a fulfilled promise back, as if the error had been dealt
        // with and replaced with a successful value.
        throw error;
      });
    },
    [logoutAction, router, authenticated, authenticatedHeaders]
  );

  return {
    // State
    authenticated,
    rehydrated,
    username,
    clientId,
    scopes,
    // Actions
    register,
    sendResetPasswordLink,
    resetPassword,
    login,
    logout,
    authenticatedRequest,
    activateAuthenticator,
    authenticateWithTOTPCode,
  };
};
